//@ts-strict-ignore
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Menu, MenuButtonProps, MenuList, MenuPopover, MenuTrigger, SplitButton } from '@fluentui/react-components';
import { useQuery } from '@apollo/client';
import Dropdown from '../../../components/connectedForm/Dropdown';
import DropdownEditor from '../../../components/DropdownEditor';
import styled from 'styled-components';
import normalizeVariables from '../../../graphql/schema/normalizeVariables';
import SalesInvoiceByPeriodQuery from '../../../graphql/queries/salesLedger/SalesInvoiceByPeriodQuery.graphql';
import SalesInvoiceByYearQuery from '../../../graphql/queries/salesLedger/SalesInvoiceByYearQuery.graphql';
import SalesInvoiceComparisonQuery from '../../../graphql/queries/salesLedger/SalesInvoiceComparisonQuery.graphql';
import SalesInvoiceYearToDateQuery from '../../../graphql/queries/salesLedger/SalesInvoiceYearToDateQuery.graphql';
import SalesAnalysisFullQuery from '../../../graphql/queries/salesLedger/SalesAnalysisFullQuery.graphql';
import multiAttributesConfigDropdown from '../../../graphql/queries/MultiAttributeConfigListQuery.graphql';
import EditableDataGrid from '../../../components/EditableDataGrid/EditableDataGrid';
import { default as Form } from '../../../components/connectedForm';
import ComponentQuery from '../../../utils/componentQuery';
import ValueCard from '../../../components/connectedForm/ValueCard';
import ReactPivot from 'react-pivot';
import { InfoButton } from '../../../components/connectedForm/Button';
import { ContextMenu, ContextMenuTrigger, MenuItem, SubMenu } from 'react-contextmenu';
import numeral from 'numeral';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import { useId } from '@fluentui/react-hooks';
import { useFullUserDetails, useRefFunction } from '../../../utils/hooks';
import getIn from '../../../components/connectedForm/utils/getIn';
import { SalesAnalyticsLine } from './SalesAnalyticsLine';
import { SalesAnalyticsBar } from './SalesAnalyticsBar';
import { SalesAnalyticsTree } from './SalesAnalyticsTree';
import { SalesAnalyticsPie } from './SalesAnalyticsPie';
import {
  useSpecificAndPreviousFinancialYear,
  useFinancialCalendars,
  useCurrentFinancialYear,
} from '../../../utils/company/useFinancialCalendars';
import { IAnalyticsColumn, IAnalyticsDimension, IAnalyticsReportOption, IWrappedMeasure } from './SalesAnalyticsTypes';
import {
  AnalysisGraphType,
  AnalysisMeasure,
  CostViewPermission,
  DashboardWidgetReportType,
  Direction,
  IFinancialCalendar,
  IFinancialPeriod,
  ISalesAnalyticsDimensionFilter,
  Maybe,
} from '../../../graphql/schema/p4types';
import { OrdinalColorScaleConfig } from '@nivo/colors';
import downloadCsv from '../../../utils/downloadCsv';

import { useProductConfig, useCompany } from '../../../utils/FetchConfig';

require('numeral/locales/en-gb');

numeral.locale('en-gb');
numeral.defaultFormat('$0,0.00');

const DRILL_INTO = 'drillInto';

export const STANDARD_MEASURES: Array<IWrappedMeasure> = [
  {
    description: 'Sales',
    value: AnalysisMeasure.Sales,
  },
  {
    description: 'Units',
    value: AnalysisMeasure.Unit,
  },
  {
    description: 'Profit',
    value: AnalysisMeasure.Profit,
  },
  {
    description: 'Cost',
    value: AnalysisMeasure.Cost,
  },
  {
    description: 'Weight',
    value: AnalysisMeasure.Weight,
  },
];

export const ANALYTICS_REPORT_TYPES: Array<IAnalyticsReportOption> = [
  {
    description: 'Leakage',
    value: DashboardWidgetReportType.YearToDate,
    graphTypes: [
      { description: AnalysisGraphType.Bar, value: AnalysisGraphType.Bar },
      { description: AnalysisGraphType.Pie, value: AnalysisGraphType.Pie },
    ],
  },
  {
    description: 'Leakage - Year to Date',
    value: DashboardWidgetReportType.ComparativeYearToDate,
    graphTypes: [
      { description: AnalysisGraphType.Bar, value: AnalysisGraphType.Bar },
      { description: AnalysisGraphType.Pie, value: AnalysisGraphType.Pie },
    ],
  },
  {
    description: 'Sales by Period',
    value: DashboardWidgetReportType.Monthly,
    graphTypes: [
      { description: AnalysisGraphType.Line, value: AnalysisGraphType.Line },
      { description: AnalysisGraphType.Areabump, value: AnalysisGraphType.Areabump },
      { description: AnalysisGraphType.Bar, value: AnalysisGraphType.Bar },
    ],
  },
  {
    description: 'Sales by Year',
    value: DashboardWidgetReportType.Yearly,
    graphTypes: [
      { description: AnalysisGraphType.Line, value: AnalysisGraphType.Line },
      { description: AnalysisGraphType.Areabump, value: AnalysisGraphType.Areabump },
      { description: AnalysisGraphType.Bar, value: AnalysisGraphType.Bar },
    ],
  },
  {
    description: 'Full Analytics',
    value: DashboardWidgetReportType.Pivot,
    graphTypes: [
      { description: AnalysisGraphType.Bubble, value: AnalysisGraphType.Bubble },
      { description: AnalysisGraphType.Map, value: AnalysisGraphType.Map },
      { description: AnalysisGraphType.Sunburst, value: AnalysisGraphType.Sunburst },
      { description: AnalysisGraphType.Bar, value: AnalysisGraphType.Bar },
    ],
  },
];

const HistoryOption = styled.span`
  cursor: pointer;
  color: blue;
`;

const useStats = (currentFinancialYear, dimensionFilters, options, dimension) => {
  const variables = {
    dimension: dimension,
    measure: 'SALES',
    financialCalendar: { ...currentFinancialYear, year: new Date().getFullYear().toString() },
    dimensionFilters: dimensionFilters,
  };

  const { data: sales } = useQuery(SalesInvoiceYearToDateQuery, { variables, ...options });
  const { data: units } = useQuery(SalesInvoiceYearToDateQuery, {
    variables: { ...variables, measure: 'UNIT' },
    ...options,
  });
  const { data: profit } = useQuery(SalesInvoiceYearToDateQuery, {
    variables: { ...variables, measure: 'PROFIT' },
    ...options,
  });

  return useMemo(
    () => ({
      statSales: sales?.data[0].value ?? 0,
      statUnits: units?.data[0].value ?? 0,
      statProfit: profit?.data[0].value ?? 0,
    }),
    [sales, units, profit]
  );
};

type IUseNormalizedState = <T = any>(initialValue: T) => [value: T, setValue: (newValue: T) => void];

const useNormalizedState: IUseNormalizedState = (initialValue) => {
  const [value, setValue] = useState(normalizeVariables(initialValue) as typeof initialValue);
  const setter = useCallback((newValue) => setValue(normalizeVariables(newValue)), [setValue]);

  return [value, setter];
};

const SalesInvoiceAnalysisHistory = ({ history, revertHistory, dimension }) => {
  return (
    <div>
      {history?.map((historyEntry, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`_${index}`}>
          <HistoryOption
            onClick={() => {
              revertHistory(historyEntry);
            }}
          >
            {historyEntry.dimension?.description}
          </HistoryOption>{' '}
          /{' '}
        </span>
      ))}
      <span>{dimension?.title}</span>
    </div>
  );
};

const SalesInvoiceAnalysisGraph = ({
  graphHeight: graphHeightFromProps,
  handleDrillFilter,
  defaultLabel,
  graphOnly,
  colorScheme,
  enableGraph,
  data,
  secondDataSet,
  graphType,
  measure,
  columns,
  previousColumns,
  pivotRows,
  reportOption,
  pivotRef,
  dimension,
  recordsInGraph = 10,
}) => {
  const recordsInGraphRestricted = useMemo(() => (recordsInGraph > 1000 ? 1000 : recordsInGraph), [recordsInGraph]);

  //Apply a limit on the scope of data, the chart can't reasonable handle more than this.
  const reducedData = useMemo(() => data?.slice(0, recordsInGraphRestricted), [data, recordsInGraphRestricted]);
  const reducedSecondaryData = useMemo(
    () => secondDataSet?.slice(0, recordsInGraphRestricted),
    [recordsInGraphRestricted, secondDataSet]
  );

  const formState = useMemo(() => {
    return {
      measure,
      columns,
      previousColumns,
      data: reducedData,
      secondDataSet: reducedSecondaryData,
      reportType: reportOption,
      pivotRows,
    };
  }, [measure, columns, previousColumns, reducedData, reducedSecondaryData, reportOption, pivotRows]);

  if ((!graphOnly && !enableGraph) || !reducedData) {
    return null;
  }

  // return null;

  //Need to account for space for drilldown controls.
  const graphHeight = handleDrillFilter ? graphHeightFromProps - 75 : graphHeightFromProps;

  switch (graphType) {
    case AnalysisGraphType.Bar:
      if (!reportOption) {
        return null;
      }
      return (
        <SalesAnalyticsBar
          formState={formState}
          height={graphHeight}
          graphOnly={!!graphOnly}
          defaultLabel={defaultLabel}
          pivotRef={pivotRef}
          colorScheme={colorScheme}
        />
      );
    case AnalysisGraphType.Line:
    case AnalysisGraphType.Areabump:
      if (!reportOption) {
        return null;
      }
      return (
        <SalesAnalyticsLine
          formState={{
            columns,
            previousColumns,
            dimension,
            data: reducedData,
            secondDataSet: reducedSecondaryData,
            graphType,
            reportType: reportOption,
          }}
          height={graphHeight}
          graphOnly={!!graphOnly}
          defaultLabel={defaultLabel}
          colorScheme={colorScheme}
        />
      );
    case AnalysisGraphType.Bubble:
    case AnalysisGraphType.Map:
    case AnalysisGraphType.Sunburst:
      return (
        <SalesAnalyticsTree
          formState={{ measure, graphType, pivotRows }}
          pivotRef={pivotRef}
          colorScheme={colorScheme}
        />
      );
    case AnalysisGraphType.Pie:
      return (
        <SalesAnalyticsPie data={reducedData} height={graphHeight} graphOnly={!!graphOnly} colorScheme={colorScheme} />
      );
    default:
      return null;
  }
};

const SalesInvoiceAnalysisGraphMenuWrapper = ({
  graph,
  setMeasure,
  setGraphType,
  revertHistory,
  getSelectableDimensions,
  handleDrillDown,
}) => {
  return (
    <>
      <ContextMenuTrigger id={'analysisMenu'}>{graph}</ContextMenuTrigger>
      <ContextMenu key={`analysisMenu`} id={`analysisMenu`}>
        {STANDARD_MEASURES.map((standardMeasure) => (
          <MenuItem
            key={standardMeasure.value}
            onClick={(e) => {
              e.preventDefault();
              setMeasure(standardMeasure);
            }}
          >
            {standardMeasure.description}
          </MenuItem>
        ))}
        <SubMenu title={'Graph Type'}>
          {ANALYTICS_REPORT_TYPES[2].graphTypes.map((newGraphType) => (
            <MenuItem key={newGraphType.value} onClick={() => setGraphType(newGraphType.value)}>
              {newGraphType.description}
            </MenuItem>
          ))}
        </SubMenu>
        <SubMenu title={'Dimension'}>
          <MenuItem
            key={'null'}
            onClick={() => {
              revertHistory();
            }}
          >
            None
          </MenuItem>
          {getSelectableDimensions().map((selectableDimension) => {
            return (
              <MenuItem
                key={selectableDimension.value}
                onClick={() => {
                  const commit: ICommit = {
                    cellKey: 'drillInto',
                    original: {
                      description: null,
                      dimensionId: null,
                    },
                    updated: {
                      drillInto: selectableDimension,
                    },
                  };
                  handleDrillDown(commit);
                }}
              >
                {selectableDimension.title}
              </MenuItem>
            );
          })}
        </SubMenu>
      </ContextMenu>
    </>
  );
};

export type IDimensionHistory = {
  dimension?: IAnalyticsDimension | null;
  dimensionFilters?: Array<Omit<ISalesAnalyticsDimensionFilter, 'relayId'>>;
  history?: Array<IDimensionHistory>;
};

type ISortDirection = Direction | 'NONE';

export enum EmbeddedSalesInvoiceOptions {
  Customer = 'CUSTOMER',
  Product = 'PRODUCT',
  DeliveryAccount = 'DELIVERY_ACCOUNT',
}

export type ISalesInvoiceAnalysisProps = {
  history?: IDimensionHistory;
  predefinedFilters?: Array<Omit<ISalesAnalyticsDimensionFilter, 'relayId'>>;
  dimension?: IAnalyticsDimension;
  reportType?: IAnalyticsReportOption;
  measure?: IWrappedMeasure;
  sortColumn?: string;
  sortDirection?: Maybe<ISortDirection>; //Sort direction for the preview grid (Analytics Form).
  dataid?: string;
  embedded?: EmbeddedSalesInvoiceOptions | undefined;
  graphRightClickEnabled?: boolean;
  graphHeight?: number;
  graphType?: Maybe<AnalysisGraphType>;
  graphOnly?: boolean;
  handleDrillFilter?: (options: IDimensionHistory) => void;
  defaultLabel?: Maybe<string>;
  colorScheme?: OrdinalColorScaleConfig;
  recordsInGraph?: number;
  currentCalendar: IFinancialCalendar; // Override for current calendar for the (period) analysis graph.
  previousCalendar: IFinancialCalendar; // Override for previous calendar for the (period) analysis graph.
};

//Expect to replace this later.
type ICommit = {
  cellKey: string;
  original: {
    description: string | null;
    dimensionId: number | null;
  };
  updated: { drillInto: IAnalyticsDimension };
};

// eslint-disable-next-line complexity
const SalesInvoiceAnalysis = (props: ISalesInvoiceAnalysisProps) => {
  const productConfig = useProductConfig();
  const { company, currentCalendar, previousCalendar } = props;

  //Get defaults from props.history - Widget option stores data filters and passes them down this way.
  const defaultDimension = useMemo(() => {
    return props.history?.dimension || props.dimension;
  }, [props.dimension, props.history?.dimension]);
  const defaultDimensionFilters = useMemo(() => {
    return props.history?.dimensionFilters || props.predefinedFilters || [];
  }, [props.predefinedFilters, props.history?.dimensionFilters]);
  const defaultHistory = useMemo(() => {
    return props.history?.history || [];
  }, [props.history]);

  //Reset the form state when changed from props - I.e. react to changes when editing widget.
  useEffect(() => {
    setDimension(defaultDimension);
    setDimensionFilters(defaultDimensionFilters || []);
    setHistory(defaultHistory);
  }, [defaultDimension, defaultDimensionFilters, defaultHistory]);

  const fullUserDetails = useFullUserDetails();
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();

  const yearToggleTooltipId = useId('yearToggleTooltipId');

  const [reportOption, setReportOption] = useState<IAnalyticsReportOption | undefined>(props.reportType);
  const [measure, setMeasure] = useState<IWrappedMeasure | undefined>(props.measure);
  const [sortColumn, setSortColumn] = useState<string | null>(props.sortColumn || null);
  const [sortDirection, setSortDirection] = useState<ISortDirection | null>(props.sortDirection ?? null);
  const [columns, setColumns] = useState<Array<IAnalyticsColumn>>([]);
  const [previousColumns, setPreviousColumns] = useState<Array<IAnalyticsColumn>>([]);
  const [data, setData] = useState<Array<any>>([]);
  const [dimensions, setDimensions] = useState<Array<IAnalyticsDimension>>([]);
  const [dimension, setDimension] = useState<IAnalyticsDimension | undefined>(defaultDimension);
  const [dimensionFilters, setDimensionFilters] =
    useState<Array<Omit<ISalesAnalyticsDimensionFilter, 'relayId'>>>(defaultDimensionFilters);

  useEffect(() => {
    setSortDirection(props.sortDirection ?? null);
  }, [props.sortDirection]);

  const qtyPrecision = productConfig?.defaultPrecision;

  const [history, setHistory] = useState<Array<IDimensionHistory> | undefined>(defaultHistory);
  const [pivotDimensions, setPivotDimensions] = useState<
    Array<{
      value: string;
      title: string;
      sortBy?: (row: object) => any;
      template?: (value: string, row: object) => string;
    }>
  >([
    {
      value: 'warehouse.description',
      title: 'Warehouse',
    },
    {
      value: 'financial_period.name',
      title: 'Financial Period',
      sortBy: (row) => {
        return row['financial_period.period_number'];
      },
    },
    {
      value: 'financial_period.year',
      title: 'Financial Year',
    },
    {
      value: 'financial_period.period_number',
      title: 'Financial Period Number',
    },
  ]);
  const pivotCalculations = useMemo(() => {
    const calculations = [
      {
        title: 'Sales',
        value: 'sales',
        template: function (val) {
          return `£${isNaN(val) ? val : val.toFixed(2)}`;
        },
        sortBy: function (row) {
          return isNaN(row.sales) ? 0 : row.sales;
        },
        className: 'pivotAlignRight',
      },
      {
        title: 'Units',
        value: 'unit',
        template: function (val) {
          return isNaN(val) ? val : val.toFixed(qtyPrecision);
        },
        sortBy: function (row) {
          return isNaN(row.unit) ? 0 : row.unit;
        },
        className: 'pivotAlignRight',
      },
      {
        title: 'Weight',
        value: 'weight',
        template: function (val) {
          return `${isNaN(val) ? val : val.toFixed(6)}`;
        },
        sortBy: function (row) {
          return isNaN(row.weight) ? 0 : row.weight;
        },
        className: 'pivotAlignRight',
      },
    ];

    if (fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All) {
      calculations.splice(1, 0, {
        title: 'Cost',
        value: 'cost',
        template: function (val) {
          return `£${isNaN(val) ? val : val.toFixed(2)}`;
        },
        sortBy: function (row) {
          return isNaN(row.cost) ? 0 : row.cost;
        },
        className: 'pivotAlignRight',
      });
      calculations.splice(3, 0, {
        title: 'Profit',
        value: 'profit',
        template: function (val) {
          return `£${isNaN(val) ? val : val.toFixed(2)}`;
        },
        sortBy: function (row) {
          return isNaN(row.profit) ? 0 : row.profit;
        },
        className: 'pivotAlignRight',
      });
      calculations.splice(4, 0, {
        title: 'Profit Percentage',
        value: 'profit_percentage',
        template: function (val) {
          return `${isNaN(val) ? val : val.toFixed(2)}%`;
        },
        sortBy: function (row) {
          return isNaN(row.profit) ? 0 : row.profit;
        },
        className: 'pivotAlignRight',
      });
    }
    return calculations;
  }, [qtyPrecision]);
  const [graphType, setGraphType] = useState<Maybe<AnalysisGraphType>>(props.graphType);

  const [enableGraph, setEnableGraph] = useState<boolean>(!!props.graphOnly);

  const [drillFilter, setDrillFilter] = useState<{ description: string; dimensionId: number } | null>(null);
  const [pivotRows, setPivotRows] = useState<Array<{ description: string }>>();
  const [secondDataSet, setSecondDataSet] = useState([]);

  const defaultNextYear = useMemo(() => {
    return getIn(connectedForm.values, `${props.dataid}.nextYear`);
  }, [connectedForm.values, props.dataid]);

  const pivotRef = useRef<any>();

  const financialCalendars = useFinancialCalendars(false);

  const [currentFinancialCalendar, setCurrentFinancialCalendar] = useNormalizedState(props.currentCalendar);

  const [previousFinancialCalendar, setPreviousFinancialCalendar] = useNormalizedState(props.previousCalendar);

  //Used to avoid loading the main query before secondary data is loaded (e.g. dimensions, calendars, etc);
  const ready = useMemo(() => {
    return dimensions && pivotDimensions && financialCalendars;
  }, [dimensions, pivotDimensions, financialCalendars]);

  //DidMount
  useEffect(() => {
    //Wrap all our initial data fetches into a single state update.
    Promise.all([generateDimensions()]).then(() => {
      //Fetch Complete
      //Not sure if any of these really still need to be async functions wrapped like this.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Not really sure that these need to be separated (Could use one bigger effect).
  useEffect(() => {
    setGraphType(props.graphType);
  }, [props.graphType]);
  useEffect(() => {
    const ro = ANALYTICS_REPORT_TYPES.find((rt) => rt.value === props.reportType?.value);
    if (ro) {
      setReportOption(ro);
    }
  }, [props.reportType]);
  useEffect(() => {
    setMeasure(props.measure);
  }, [props.measure, props.measure?.value]);

  const { statSales, statUnits, statProfit } = useStats(
    currentFinancialCalendar,
    dimensionFilters,
    {
      skip: props.graphOnly,
    },
    dimension?.value
  );

  const generateDimensions = async () => {
    const newDimensions = [
      {
        title: 'Products',
        value: 'PRODUCT',
      },
      {
        title: 'Warehouse',
        value: 'WAREHOUSE',
      },
      {
        title: 'Customer',
        value: 'CUSTOMER',
      },
      {
        title: 'Delivery_Account',
        value: 'DELIVERY_ACCOUNT',
      },
    ];

    const newPivotDimensions = [...pivotDimensions];

    if (props.embedded !== EmbeddedSalesInvoiceOptions.Customer) {
      newPivotDimensions.push({
        title: 'Invoice Customer Reference',
        value: 'customer.reference',
        template: (value, row) => {
          return `<a href='/Customers/Customer/Details/${row['customer.id']}' target ='_blank'>${value}</a>`;
        },
      });
      newPivotDimensions.push({
        value: 'customer.name',
        title: 'Invoice Customer',
        template: (value, row) => {
          return `<a href='/Customers/Customer/Details/${row['customer.id']}' target ='_blank'>${value}</a>`;
        },
      });
    }

    if (props.embedded !== EmbeddedSalesInvoiceOptions.DeliveryAccount) {
      newPivotDimensions.push({
        title: 'Delivery Account Reference',
        value: 'delivery_account.reference',
        template: (value, row) => {
          return `<a href='/Customers/Customer/Details/${row['delivery_account.id']}' target ='_blank'>${value}</a>`;
        },
      });
      newPivotDimensions.push({
        title: 'Delivery Account Name',
        value: 'delivery_account.name',
        template: (value, row) => {
          return `<a href='/Customers/Customer/Details/${row['delivery_account.id']}' target ='_blank'>${value}</a>`;
        },
      });
    }

    if (props.embedded !== EmbeddedSalesInvoiceOptions.Product) {
      newPivotDimensions.push({
        value: 'product.reference',
        title: 'Product Reference',
        template: (value, row) => {
          return `<a href='/Products/Product/Details/${row['product.id']}' target ='_blank'>${value}</a>`;
        },
      });
      newPivotDimensions.push({
        value: 'product.description',
        title: 'Product Description',
        template: (value, row) => {
          return `<a href='/Products/Product/Details/${row['product.id']}' target ='_blank'>${value}</a>`;
        },
      });
    }

    await ComponentQuery(multiAttributesConfigDropdown, {}, true).then(({ response }) => {
      if (response && response.salesConfigs && response.customerConfigs && response.productConfigs) {
        response.salesConfigs.edges.forEach((edge) => {
          if (edge.node.analysisRequired === true) {
            newDimensions.push({
              title: edge.node.title,
              value: `DIMENSION${edge.node.dimensionIndex}`,
            });
            newPivotDimensions.push({
              title: edge.node.title,
              value: `dimension.description${edge.node.dimensionIndex}`,
            });
          }
        });
        response.customerConfigs.edges.forEach((edge) => {
          if (edge.node.analysisRequired === true) {
            newDimensions.push({
              title: edge.node.title,
              value: `CUSTOMER_DIMENSION${edge.node.dimensionIndex}`,
            });
            newPivotDimensions.push({
              title: edge.node.title,
              value: `customer_dimension.description${edge.node.dimensionIndex}`,
            });
          }
        });
        response.productConfigs.edges.forEach((edge) => {
          if (edge.node.analysisRequired === true) {
            newDimensions.push({
              title: edge.node.title,
              value: `PRODUCT_DIMENSION${edge.node.dimensionIndex}`,
            });
            newPivotDimensions.push({
              title: edge.node.title,
              value: `product_dimension.description${edge.node.dimensionIndex}`,
            });
          }
        });
      }
    });

    setDimensions(newDimensions);
    setPivotDimensions(newPivotDimensions);
  };

  const getSelectableDimensions = useRefFunction(() => {
    const traversedDimensions: Array<Maybe<string>> = [];

    if (dimensionFilters) {
      dimensionFilters.forEach((filter) => {
        traversedDimensions.push(filter.dimension);
      });
    }

    const selectableDimensions: Array<IAnalyticsDimension> = [];

    dimensions.forEach((thisDimension) => {
      if (thisDimension.value === dimension?.value) return;
      if (thisDimension?.value && traversedDimensions.includes(thisDimension.value)) return;
      selectableDimensions.push(thisDimension);
    });

    return selectableDimensions;
  });

  const handleMeasureChange = (newMeasure) => {
    if (newMeasure === null) return;

    setMeasure(newMeasure);
  };

  const handleCurrentYearChange = (newCalendar) => {
    if (newCalendar === null) return;
    const currentFinancialYearIndex = (financialCalendars as Array<Maybe<IFinancialCalendar>>).findIndex(
      (cal) => cal?.id === newCalendar.id
    );
    const newPreviousFinancialYear =
      currentFinancialYearIndex > 0
        ? (financialCalendars as Array<Maybe<IFinancialCalendar>>)[currentFinancialYearIndex - 1]
        : null;

    setCurrentFinancialCalendar(newCalendar);
    setPreviousFinancialCalendar(newPreviousFinancialYear);
  };

  const toggleWidgetYear = useCallback(
    (checked) => {
      if (props.currentCalendar || props.previousCalendar) {
        // If current/previous calendar are being provided by props, prevent unintentional reset - toggleWidgetYear may be called from a useEffect
        return;
      }
      if (checked) {
        const currentFinancialYearIndex = financialCalendars.findIndex(
          (cal) => cal?.id === company.currentFinancialYear?.id
        );
        const newNextFinancialYear =
          currentFinancialYearIndex > 0 && currentFinancialYearIndex < financialCalendars?.length
            ? (financialCalendars as Array<Maybe<IFinancialCalendar>>)[currentFinancialYearIndex + 1]
            : null;
        setCurrentFinancialCalendar(newNextFinancialYear);
        setPreviousFinancialCalendar(company.currentFinancialYear);
      } else {
        setCurrentFinancialCalendar(company.currentFinancialYear);
        setPreviousFinancialCalendar(company.previousFinancialYear);
      }
    },
    [
      company.currentFinancialYear,
      company.previousFinancialYear,
      financialCalendars,
      setCurrentFinancialCalendar,
      setPreviousFinancialCalendar,
    ]
  );

  useEffect(() => {
    if (financialCalendars && financialCalendars.length > 0) {
      toggleWidgetYear(defaultNextYear);
    }
  }, [defaultNextYear, financialCalendars, toggleWidgetYear]);

  const handleReportTypeChange = (newReportType) => {
    if (newReportType === null) return;
    setReportOption(newReportType);
    setGraphType(newReportType.graphTypes[0].value);
  };

  const handleGraphChange = (newGraphType) => {
    if (newGraphType === null) return;
    setGraphType(newGraphType.value);
  };

  interface ICommonVariables {
    dimension: string;
    measure: string;
    dimensionFilters: Array<IAnalyticsDimension>;
    sortDirection: Direction;
  }

  const { query, variables } = useMemo(() => {
    let thisQuery;
    let thisVariables:
      | (ICommonVariables & {
          financialCalendar: Maybe<IFinancialCalendar>;
          includePrevious: boolean;
          previousFinancialCalendar: Maybe<IFinancialCalendar>;
        })
      | (ICommonVariables & {
          leftComparison: Maybe<IFinancialCalendar>;
          rightComparison: Maybe<IFinancialCalendar>;
          comparative: boolean;
        })
      | (ICommonVariables & {
          financialCalendar: Maybe<IFinancialCalendar>;
        }) = {
      dimension: dimension?.value,
      measure: measure?.value,
      dimensionFilters: dimensionFilters.slice(),
    } as any;

    if (reportOption?.value === DashboardWidgetReportType.Monthly) {
      thisQuery = SalesInvoiceByPeriodQuery;

      thisVariables = {
        ...thisVariables,
        financialCalendar: currentFinancialCalendar,
        // For Customer/Product overviews (graphOnly), fetch previous years period data for comparison.
        includePrevious: !!props.graphOnly,
        previousFinancialCalendar: previousFinancialCalendar,
      };
    }

    if (reportOption?.value === DashboardWidgetReportType.Yearly) {
      thisQuery = SalesInvoiceByYearQuery;
    }

    if (
      reportOption?.value === DashboardWidgetReportType.YearToDate ||
      reportOption?.value === DashboardWidgetReportType.ComparativeYearToDate
    ) {
      thisQuery = SalesInvoiceComparisonQuery;

      thisVariables = {
        ...thisVariables,
        leftComparison: previousFinancialCalendar,
        rightComparison: currentFinancialCalendar,
        comparative: reportOption.value === DashboardWidgetReportType.ComparativeYearToDate,
        sortDirection: sortDirection === 'NONE' || sortDirection === null ? Direction.Asc : sortDirection,
      };
    }

    if (reportOption?.value === DashboardWidgetReportType.Pivot) {
      thisQuery = SalesAnalysisFullQuery;

      thisVariables = {
        ...thisVariables,
        financialCalendar: currentFinancialCalendar,
      };
    }

    return { query: thisQuery, variables: thisVariables };
  }, [
    currentFinancialCalendar,
    dimension?.value,
    dimensionFilters,
    measure?.value,
    previousFinancialCalendar,
    props.graphOnly,
    reportOption?.value,
    sortDirection,
  ]);

  //TODO - Obey sortDirection....
  useQuery(query, {
    variables,
    skip: !ready,
    fetchPolicy: 'network-only',
    onCompleted: (mainData) => {
      const { columns: newColumns, previousColumns: newPreviousColumns } = processColumns({
        dimension,
        reportType: reportOption,
        data: mainData,
      });

      const { data: sortedData } = mainData.data ? initialSort(mainData.data) : { data: [] };
      const { data: sortedPreviousData } = mainData.previousData ? initialSort(mainData.previousData) : { data: [] };

      setColumns(newColumns);
      setPreviousColumns(newPreviousColumns);
      setData(sortedData);
      setSecondDataSet(sortedPreviousData);
    },
  });

  const processColumns = ({ dimension: newDimension, reportType: newReportType, data: mainData }) => {
    const newColumns: Array<IAnalyticsColumn> = [];
    const newPreviousColumns: Array<IAnalyticsColumn> = [];

    if (newDimension !== null && (newDimension.value === 'CUSTOMER' || newDimension.value === 'PRODUCT')) {
      newColumns.push({
        key: newReportType.value === DashboardWidgetReportType.Monthly ? 'ref' : 'reference',
        name: 'Reference',
        sortable: true,
        editable: false,
        filterable: true,
      });
    }

    if (newDimension?.value) {
      newColumns.push({
        key: 'description',
        name: !newDimension ? '' : newDimension.title,
        sortable: true,
        editable: false,
        filterable: true,
      });
    }
    if (newReportType.value === DashboardWidgetReportType.Monthly) {
      currentFinancialCalendar?.financialPeriodList?.forEach((period: IFinancialPeriod) => {
        newColumns.push({
          key: `period_${period.id}`,
          name: period.name!,
          shortName: period.shortName!,
          sortable: true,
          editable: false,
          filterable: true,
          formatter: 'numeric',
          periodNumber: period.periodNumber,
        });
      });
      if (previousFinancialCalendar) {
        previousFinancialCalendar.financialPeriodList?.forEach((period: IFinancialPeriod) => {
          newPreviousColumns.push({
            key: `period_${period.id}`,
            name: period.name!,
            shortName: period.shortName!,
            sortable: true,
            editable: false,
            filterable: true,
            formatter: 'numeric',
            periodNumber: period.periodNumber,
          });
        });
      }
    }
    if (newReportType.value === DashboardWidgetReportType.Yearly) {
      financialCalendars.forEach((calendar) => {
        const index = mainData?.data?.[0]?.yearValueList?.findIndex((yvl) => yvl.financialYear === calendar?.year);

        newColumns.push({
          key: `yearValueList.${index}.value`,
          name: calendar?.year || 'undefined',
          shortName: calendar?.year || 'undefined',
          sortable: true,
          editable: false,
          filterable: true,
          formatter: 'numeric',
          index: index,
        });
      });
    }

    if (
      (newReportType.value === DashboardWidgetReportType.YearToDate ||
        newReportType.value === DashboardWidgetReportType.ComparativeYearToDate) &&
      previousFinancialCalendar
    ) {
      newColumns.push({
        key: 'left_comparison_value',
        name: `${measure?.description} ${previousFinancialCalendar?.year}`,
        sortable: true,
        editable: false,
        formatter: 'numeric',
        options: {
          format: {
            thousandSeparator: true,
          },
        },
      });

      newColumns.push({
        key: 'right_comparison_value',
        name: `${measure?.description} ${currentFinancialCalendar?.year}`,
        sortable: true,
        editable: false,
        formatter: 'numeric',
        options: {
          format: {
            thousandSeparator: true,
          },
        },
      });

      newColumns.push({
        key: 'difference',
        name: 'Variance',
        sortable: true,
        editable: false,
        formatter: 'numeric',
        options: {
          format: {
            thousandSeparator: true,
          },
        },
      });
    }

    newColumns.push({
      name: 'Drill Down',
      key: DRILL_INTO,
      editor: <DropdownEditor />,
      options: {
        dropdown: {
          labelKey: 'title',
          valueKey: 'value',
          options: getSelectableDimensions,
          clearButton: false,
        },
      } as any,
      editable: true,
      width: 200,
      resizable: false,
      formatter: 'dropdown',
      sortable: false,
    });

    return { columns: newColumns, previousColumns: newPreviousColumns };
  };

  const initialSort = (gridData) => {
    return handleGridSort(sortColumn, sortDirection, gridData);
  };

  const handleGridSort = (newSortColumn, newSortDirection, gridData) => {
    let sortedData;
    if (newSortDirection !== 'NONE') {
      const basicComparator = (a, b) => {
        const left = isNaN(a[newSortColumn]) ? a[newSortColumn] : parseFloat(a[newSortColumn]);
        const right = isNaN(b[newSortColumn]) ? b[newSortColumn] : parseFloat(b[newSortColumn]);

        if (newSortDirection === 'ASC') {
          return left > right ? 1 : -1;
        } else if (newSortDirection === 'DESC') {
          return left < right ? 1 : -1;
        }
      };
      sortedData = gridData.slice(0).sort(basicComparator);
    } else {
      sortedData = gridData;
    }

    return {
      data: sortedData,
      sortColumn: newSortColumn,
      sortDirection: newSortDirection,
    };
  };

  const handleGridSortAndUpdateState = (updatedSortColumn, updatedSortDirection) => {
    const {
      data: newData,
      sortColumn: newSortColumn,
      sortDirection: newSortDirection,
    } = handleGridSort(updatedSortColumn, updatedSortDirection, data);
    setData(newData);
    setSortColumn(newSortColumn);
    setSortDirection(newSortDirection);
  };

  const rowUpdated = (commit) => {
    if (commit.cellKey === DRILL_INTO) {
      handleDrillDown(commit);
    }
  };

  const handleDrillDown = (commit: ICommit) => {
    if (!commit.updated.drillInto) {
      return;
    }

    let drillIntoDimension: IAnalyticsDimension | null = null;
    const newDimensionFilters = dimensionFilters.slice();
    const newHistory: Array<IDimensionHistory> = history || [];

    let currentDescription;
    if (dimension && dimension.value === null) {
      currentDescription = 'Total';
    } else {
      currentDescription = commit.original?.description;
    }

    newHistory.push({
      dimension: {
        ...(dimension || {}),
        description: currentDescription,
      },
      dimensionFilters: dimensionFilters.slice(),
      history: newHistory.slice(),
    });

    if (commit.updated && commit.updated.drillInto) {
      drillIntoDimension = commit.updated.drillInto;
    }

    if (commit.original?.dimensionId && dimension?.value) {
      newDimensionFilters.push({
        dimension: dimension.value,
        id: commit.original.dimensionId,
      });
    }

    if (props.handleDrillFilter) {
      //Parent handling drill state (Widget), data will update when values trickle back down.
      setDrillFilter(null);
      props.handleDrillFilter({
        dimensionFilters: newDimensionFilters,
        dimension: drillIntoDimension,
        history: newHistory,
      });
    } else {
      setDrillFilter(null);
      setDimensionFilters(newDimensionFilters);
      setDimension(drillIntoDimension || undefined);
      setHistory(newHistory);
    }
  };

  const revertHistory = (updatedHistory = history?.[0]) => {
    if (props.handleDrillFilter && updatedHistory) {
      props.handleDrillFilter(updatedHistory);
      return;
    }

    setDimensionFilters(updatedHistory?.dimensionFilters?.slice() || []);
    setDimension(updatedHistory?.dimension || undefined);
    setHistory(updatedHistory?.history?.slice());
  };

  const pivotReduce = (row, memo) => {
    memo['customer.id'] = row['customer.id'];
    memo['product.id'] = row['product.id'];
    memo['delivery_account.id'] = row['delivery_account.id'];
    memo.sales = (memo.sales || 0) + parseFloat(row.sales);
    memo.cost = (memo.cost || 0) + parseFloat(row.cost);
    memo.profit = (memo.profit || 0) + (isNaN(row.profit) ? 0 : parseFloat(row.profit));
    memo.unit = (memo.unit || 0) + parseFloat(row.unit);
    memo.weight = (memo.weight || 0) + parseFloat(row.weight);

    memo['profit_percentage'] =
      company?.salesOrCost === 'SALE'
        ? (memo.profit * 100) / (memo.sales ?? 1)
        : (memo.profit * 100) / (memo.cost ?? 1);

    memo['financial_period.period_number'] = parseInt(row['financial_period.period_number'], 10);
    return memo;
  };

  const graph = (
    <SalesInvoiceAnalysisGraph
      graphHeight={props.graphHeight}
      handleDrillFilter={props.handleDrillFilter}
      defaultLabel={props.defaultLabel}
      graphOnly={props.graphOnly}
      colorScheme={props.colorScheme}
      enableGraph={enableGraph}
      data={data}
      secondDataSet={secondDataSet}
      graphType={graphType}
      measure={measure}
      columns={columns}
      previousColumns={previousColumns}
      pivotRows={pivotRows}
      reportOption={reportOption}
      pivotRef={pivotRef}
      dimension={dimension}
      recordsInGraph={props.recordsInGraph}
    />
  );

  const downloadWithTotals = () => {
    if (pivotRef.current === null) return;
    pivotRef.current.downloadCSV(pivotRef.current.state.rows);
  };

  /**
   * Roughly pulled over from ReactPivot for consistent results with pivot table exports.
   */
  const download = () => {
    let filename,
      header = 'title',
      newColumns,
      rows;
    if (reportOption?.value !== DashboardWidgetReportType.Pivot) {
      newColumns = columns.filter((col) => col.key !== 'drillInto');
      rows = data;
      header = 'name';
      filename = `${reportOption?.description} - ${measure?.description} - ${history
        ?.map((historyEntry) => historyEntry.dimension?.description)
        .join(' / ')} / ${dimension?.title}.csv`;
    } else {
      if (pivotRef.current === null) return;
      const pivotStateDimensions = pivotRef.current?.state.dimensions;

      newColumns = pivotStateDimensions.map((title) => ({ key: title, title: title }));

      pivotCalculations
        .filter((c) => pivotRef.current.state.hiddenColumns.indexOf(c.title) < 0)
        .forEach((c) =>
          newColumns.push({
            title: c.title,
            key: c.value,
          })
        );

      rows = pivotRows;

      filename = `${reportOption.description} - ${pivotStateDimensions.join(' - ')}.csv`;
    }

    const table: Array<Array<string>> = [];
    table[0] = newColumns.map((col) => col[header]);

    rows.forEach((row) => {
      table.push(newColumns.map((col) => getIn(row, col.key)));
    });

    return downloadCsv(table, filename);
  };

  if (props.graphOnly) {
    const graphElement = props.graphRightClickEnabled ? (
      <SalesInvoiceAnalysisGraphMenuWrapper
        graph={graph}
        revertHistory={revertHistory}
        getSelectableDimensions={getSelectableDimensions}
        setGraphType={setGraphType}
        handleDrillDown={handleDrillDown}
        setMeasure={setMeasure}
      />
    ) : (
      graph
    );

    if (props.handleDrillFilter && data) {
      return (
        <Form.Fieldset cols={1}>
          <SalesInvoiceAnalysisHistory history={history} revertHistory={revertHistory} dimension={dimension} />
          <Form.Row>
            <Form.Fieldset cols={1 / 4}>
              <Dropdown
                id={'filters'}
                label={'Drill Filter'}
                options={data.map((entry) => {
                  return entry;
                })}
                valueKey="dimensionId"
                labelKey="description"
                dynamicOptions={true}
                value={drillFilter}
                onChange={setDrillFilter}
              />
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 4}>
              <Dropdown
                id="drill"
                label="Drill Into"
                options={getSelectableDimensions()}
                dynamicOptions={true}
                value={null}
                valueKey="value"
                labelKey="title"
                onChange={(newDimension) => {
                  const commit: ICommit = {
                    cellKey: 'drillInto',
                    original: {
                      description: drillFilter ? drillFilter.description : null,
                      dimensionId: drillFilter ? drillFilter.dimensionId : null,
                    },
                    updated: {
                      drillInto: newDimension,
                    },
                  };

                  handleDrillDown(commit);
                }}
                clearable={false}
                deletable={false}
              />
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 4}>
              <TooltipHost content="Choose between current and next year for analytics data." id={yearToggleTooltipId}>
                <Toggle
                  checked={defaultNextYear}
                  onText="Next Year"
                  offText="Current Year"
                  onChange={(event, checked) => {
                    dispatch(connectedForm.change(`${props.dataid}.nextYear`, checked));
                    toggleWidgetYear(checked);
                  }}
                  aria-describedby={yearToggleTooltipId}
                />
              </TooltipHost>
            </Form.Fieldset>
            <Form.Fieldset cols={1 / 4}>
              <InfoButton
                onClick={() => {
                  props.handleDrillFilter?.({});
                }}
              >
                {' '}
                Reset Drilldown{' '}
              </InfoButton>
            </Form.Fieldset>
          </Form.Row>
          <div style={{ height: '100%', width: '100%' }}>
            <Form.Row>{graphElement}</Form.Row>
          </div>
        </Form.Fieldset>
      );
    }
    return graphElement;
  }

  let measureDropdownOptions: IWrappedMeasure[] = STANDARD_MEASURES;
  if (fullUserDetails?.companyUser?.costViewPermission !== CostViewPermission.All) {
    measureDropdownOptions = measureDropdownOptions.filter(
      (measure) => measure.value !== AnalysisMeasure.Profit && measure.value !== AnalysisMeasure.Cost
    );
  }
  if (reportOption?.value === DashboardWidgetReportType.Pivot) {
    measureDropdownOptions = measureDropdownOptions.filter((measure) => measure.value !== AnalysisMeasure.Profit);
  }

  return (
    <>
      <Form.Row>
        <Form.Fieldset cols={1}>
          <Form.Legend label={`Statistics (${currentFinancialCalendar?.year})`} />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 12}>
          <ValueCard value={statSales} label={'Sales Year to Date'} currency={true} type={'baseCurrency'} />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 12}>
          <ValueCard value={statUnits} label={'Units Year to Date'} decimals={2} />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 12}>
          <ValueCard value={statProfit} label={'Profit Year to Date'} currency={true} type={'baseCurrency'} />
        </Form.Fieldset>
      </Form.Row>
      <Form.Row>
        <Form.Fieldset cols={1}>
          <Form.Legend label="Breakdown" />
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 6}>
          <Dropdown
            id="reportType"
            label="Report Type"
            options={ANALYTICS_REPORT_TYPES}
            value={reportOption}
            valueKey="value"
            labelKey="description"
            onChange={handleReportTypeChange}
            clearable={false}
            deletable={false}
            labelCols={2 / 5}
            inputCols={3 / 5}
          />
        </Form.Fieldset>
        {reportOption?.value === DashboardWidgetReportType.Monthly ||
        reportOption?.value === DashboardWidgetReportType.Yearly ||
        reportOption?.value === DashboardWidgetReportType.YearToDate ||
        reportOption?.value === DashboardWidgetReportType.ComparativeYearToDate ||
        (reportOption?.value === DashboardWidgetReportType.Pivot && enableGraph) ? (
          <Form.Fieldset cols={1 / 6}>
            <Dropdown
              id="analysisMeasure"
              label="Measure"
              options={measureDropdownOptions}
              value={measure}
              valueKey="value"
              labelKey="description"
              onChange={handleMeasureChange}
              clearable={false}
              deletable={false}
              labelCols={2 / 5}
              inputCols={3 / 5}
            />
          </Form.Fieldset>
        ) : null}
        {reportOption?.value === DashboardWidgetReportType.YearToDate ||
        reportOption?.value === DashboardWidgetReportType.ComparativeYearToDate ||
        reportOption?.value === DashboardWidgetReportType.Monthly ||
        reportOption?.value === DashboardWidgetReportType.Pivot ? (
          <Form.Fieldset cols={1 / 6}>
            <Dropdown
              id="financialCalendar"
              label="Current Year"
              options={financialCalendars}
              value={currentFinancialCalendar}
              valueKey="id"
              labelKey="year"
              onChange={handleCurrentYearChange}
              clearable={false}
              deletable={false}
              labelCols={2 / 5}
              inputCols={3 / 5}
              rawLabel={true}
            />
          </Form.Fieldset>
        ) : null}
        {enableGraph && reportOption && reportOption.graphTypes.length > 1 ? (
          <Form.Fieldset cols={1 / 6}>
            <Dropdown
              id="graphType"
              label="Graph Type"
              options={reportOption.graphTypes}
              value={graphType}
              valueKey="value"
              labelKey="description"
              onChange={handleGraphChange}
              clearable={false}
              deletable={false}
              labelCols={2 / 5}
              inputCols={3 / 5}
            />
          </Form.Fieldset>
        ) : null}
        <Form.Fieldset cols={1 / 8}>
          <InfoButton onClick={() => setEnableGraph(!enableGraph)}>{enableGraph ? 'Hide' : 'Display'} Graph</InfoButton>
        </Form.Fieldset>
        <Form.Fieldset cols={1 / 8}>
          {reportOption?.value === DashboardWidgetReportType.Pivot ? (
            <div style={{ marginTop: '6px' }}>
              <Menu positioning="below-end">
                <MenuTrigger disableButtonEnhancement>
                  {(triggerProps: MenuButtonProps) => (
                    <SplitButton
                      menuButton={triggerProps}
                      primaryActionButton={{
                        onClick: () => {
                          download();
                        },
                      }}
                    >
                      Export as CSV
                    </SplitButton>
                  )}
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem
                      onClick={() => {
                        downloadWithTotals();
                      }}
                    >
                      Export as CSV with Totals
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            </div>
          ) : (
            <InfoButton onClick={download}>Export as CSV</InfoButton>
          )}
        </Form.Fieldset>
      </Form.Row>
      <Form.Row>{graph}</Form.Row>
      <Form.Row>
        {reportOption?.value === DashboardWidgetReportType.Pivot ? (
          <ReactPivot
            ref={pivotRef}
            rows={data}
            dimensions={pivotDimensions}
            reduce={pivotReduce}
            calculations={pivotCalculations}
            compact={true}
            nPaginateRows={10000}
            soloText="Filter"
            activeDimensions={['Financial Period']}
            sortBy={'Financial Period'}
            csvDownloadEnabled={false}
            onData={(rows) => {
              const dimensions = pivotRef.current?.state?.dimensions || [];
              const newFilteredRows = rows.filter((row) => {
                //null specifically allowed, just removing undefined (total rows).
                return dimensions.every((dimension) => row[dimension] !== undefined);
              });
              setPivotRows(newFilteredRows);
            }}
          />
        ) : null}
        {reportOption?.value === DashboardWidgetReportType.Monthly ||
        reportOption?.value === DashboardWidgetReportType.Yearly ||
        reportOption?.value === DashboardWidgetReportType.YearToDate ||
        reportOption?.value === DashboardWidgetReportType.ComparativeYearToDate ? (
          <Form.Fieldset cols={1}>
            <SalesInvoiceAnalysisHistory history={history} revertHistory={revertHistory} dimension={dimension} />
            <div>{data.length}</div>
            <EditableDataGrid
              columns={columns}
              listData={data}
              rowsCount={data.length}
              onGridSort={handleGridSortAndUpdateState}
              enableCellSelect={true}
              enableCellAutoFocus={false}
              onRowUpdated={rowUpdated}
              ignoreContainerWidth={true}
              contextKey={'sales-invoice-analysis'}
            />
          </Form.Fieldset>
        ) : null}
      </Form.Row>
      <Form.Row>
        <div style={{ height: 50 }} />
      </Form.Row>
    </>
  );
};

SalesInvoiceAnalysis.defaultProps = {
  reportType: ANALYTICS_REPORT_TYPES[0],
  measure: STANDARD_MEASURES[0],
  dimension: {
    value: null,
    title: 'Total',
    description: 'Total',
  },
  graphHeight: 500,
  graphRightClickEnabled: false,
  graphType: AnalysisGraphType.Bar,
  handleDrillFilter: null,
  history: {},
};

export default (props: ISalesInvoiceAnalysisProps) => {
  const company = useCompany();
  let currentFinancialYear: number = new Date().getFullYear();
  let previousFinancialYear: number = new Date().getFullYear();

  const fallbackCurrentFinancialCalendar = useCurrentFinancialYear();

  if (company) {
    if (company.currentCalendarYearEnabled !== undefined && company.currentCalendarYearEnabled) {
      currentFinancialYear = fallbackCurrentFinancialCalendar?.year ?? currentFinancialYear;
      previousFinancialYear = currentFinancialYear - 1;
    } else {
      currentFinancialYear = company.currentFinancialYear?.year ?? currentFinancialYear;
      previousFinancialYear = company.previousFinancialYear?.year ?? previousFinancialYear;
    }
  }

  const { current: currentCalendar, previous: previousCalendar } = useSpecificAndPreviousFinancialYear(
    currentFinancialYear,
    previousFinancialYear
  );

  // Check if the calendars have fetched yet.
  const calendarsLoaded: boolean = !!currentCalendar || !!previousCalendar;

  if (!company || !calendarsLoaded) {
    return null;
  }
  return (
    <SalesInvoiceAnalysis
      {...props}
      company={company}
      currentCalendar={currentCalendar}
      previousCalendar={previousCalendar}
      calendarsLoaded={calendarsLoaded}
    />
  );
};
